import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../constants";
import {
  DREAM_COMPANY_GROUPS,
  isListedDreamCompany,
  resolveDreamCompany,
} from "../constants/dreamCompanies";
import { listDreamCompanies, checkDreamCompany, addDreamCompany } from "../api";

/**
 * Professional dream-company picker — portal dropdown (never clipped by parents).
 */
export default function DreamCompanyPicker({
  value = "",
  onChange,
  inputStyle = {},
}) {
  const company = resolveDreamCompany(value);
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState(null);
  const [query, setQuery] = useState("");
  const [community, setCommunity] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [check, setCheck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadCommunity = async () => {
    try {
      const res = await listDreamCompanies();
      if (res.success) setCommunity(res.community || []);
    } catch {
      /* offline */
    }
  };

  useEffect(() => {
    loadCommunity();
  }, []);

  const placeMenu = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 280);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 320 && r.top > spaceBelow;
    setMenuBox({
      left: Math.max(8, left),
      width,
      top: openUp ? undefined : r.bottom + 6,
      bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
      maxHeight: Math.min(340, openUp ? r.top - 16 : spaceBelow - 12),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placeMenu();
    const onWin = () => placeMenu();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open]);

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DREAM_COMPANY_GROUPS.map((g) => ({
      ...g,
      companies: g.companies.filter((c) => !q || c.toLowerCase().includes(q)),
    })).filter((g) => g.companies.length);
  }, [query]);

  const filteredCommunity = useMemo(() => {
    const q = query.trim().toLowerCase();
    return community.filter((c) => !q || c.name.toLowerCase().includes(q));
  }, [community, query]);

  const pick = (name) => {
    onChange?.(name);
    setOpen(false);
    setQuery("");
  };

  const openOther = () => {
    setOpen(false);
    setDraft("");
    setCheck(null);
    setError("");
    setModalOpen(true);
  };

  const runCheck = async (name) => {
    setError("");
    try {
      const res = await checkDreamCompany(name);
      setCheck(res);
      return res;
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setCheck(e.response?.data || null);
      return e.response?.data || null;
    }
  };

  const saveOther = async (force = false) => {
    const name = draft.trim();
    if (!name) {
      setError("Enter a company name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await addDreamCompany(name, force);
      if (res.success && res.company?.name) {
        await loadCommunity();
        onChange?.(res.company.name);
        setModalOpen(false);
        setDraft("");
        setCheck(null);
      } else if (res.needsConfirm) {
        setCheck(res);
      } else {
        setError(res.error || "Could not save");
        setCheck(res);
      }
    } catch (e) {
      const data = e.response?.data;
      if (data?.needsConfirm) setCheck(data);
      else {
        setError(data?.error || e.message || "Could not save");
        setCheck(data || null);
      }
    }
    setSaving(false);
  };

  const display = company || "Select dream company…";
  const isCustom = !!(company && !isListedDreamCompany(company));

  const menu = open && menuBox
    ? createPortal(
      <>
        <div
          style={{ position: "fixed", inset: 0, zIndex: 1200 }}
          onClick={() => setOpen(false)}
        />
        <div
          style={{
            position: "fixed",
            zIndex: 1201,
            left: menuBox.left,
            width: menuBox.width,
            top: menuBox.top,
            bottom: menuBox.bottom,
            maxHeight: menuBox.maxHeight,
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{
            padding: "10px 12px",
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
            background: "#F8FAFC",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
              fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: 0.04, textTransform: "uppercase",
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: C.warning, boxShadow: `0 0 0 3px ${C.warningBg}` }} />
              Dream company
            </div>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search MAANG, Big Tech, India MNC…"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 13,
                border: `1px solid ${C.borderStrong}`,
                borderRadius: 8,
                boxSizing: "border-box",
                outline: "none",
                background: "#fff",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: 6 }}>
            {filteredGroups.map((g) => (
              <div key={g.label} style={{ marginBottom: 4 }}>
                <div style={{
                  padding: "8px 12px 4px",
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#9A7200",
                }}>
                  {g.label}
                </div>
                {g.companies.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pick(c)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      marginBottom: 2,
                      border: "none",
                      borderRadius: 10,
                      background: company === c
                        ? `linear-gradient(90deg, ${C.warningBg} 0%, ${C.primarySoft}CC 100%)`
                        : "transparent",
                      color: company === c ? "#9A7200" : C.text,
                      fontWeight: company === c ? 750 : 600,
                      fontSize: 13.5,
                      cursor: "pointer",
                      boxShadow: company === c ? `inset 3px 0 0 ${C.warning}` : "none",
                    }}
                  >
                    <span style={{
                      width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                      background: company === c ? C.warning : "#fff",
                      border: `1.5px solid ${C.warning}55`,
                      color: company === c ? "#fff" : C.warning,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {company === c ? <i className="ti ti-check" style={{ fontSize: 13 }} /> : <i className="ti ti-building" style={{ fontSize: 12 }} />}
                    </span>
                    {c}
                  </button>
                ))}
              </div>
            ))}

            {filteredCommunity.length > 0 && (
              <div style={{ marginTop: 4, borderTop: `1px solid ${C.border}`, paddingTop: 4 }}>
                <div style={{
                  padding: "8px 12px 4px",
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.muted,
                }}>
                  Community · added by students
                </div>
                {filteredCommunity.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => pick(c.name)}
                    style={{
                      display: "flex",
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      marginBottom: 2,
                      border: "none",
                      borderRadius: 10,
                      background: company === c.name ? C.primarySoft : "transparent",
                      color: C.text,
                      fontSize: 13.5,
                      cursor: "pointer",
                      justifyContent: "space-between",
                      gap: 8,
                      fontWeight: company === c.name ? 750 : 600,
                      boxShadow: company === c.name ? `inset 3px 0 0 ${C.primary}` : "none",
                    }}
                  >
                    <span>{c.name}</span>
                    <span style={{ fontSize: 10.5, color: C.muted }}>×{c.useCount || 1}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ borderTop: `1px solid ${C.border}`, padding: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={openOther}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1.5px dashed ${C.primary}`,
                background: C.primarySoft,
                color: C.primary,
                fontWeight: 750,
                fontSize: 12.5,
                cursor: "pointer",
              }}
            >
              + Other — add a new company for everyone
            </button>
          </div>
        </div>
      </>,
      document.body
    )
    : null;

  const modal = modalOpen
    ? createPortal(
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          background: "rgba(15,23,42,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
        onClick={() => !saving && setModalOpen(false)}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            borderRadius: 14,
            border: `1px solid ${C.border}`,
            boxShadow: "0 20px 50px rgba(15,23,42,0.2)",
            padding: 20,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 4 }}>
            Add company for everyone
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 14, lineHeight: 1.45 }}>
            Saved to the shared list. We flag lookalikes of MAANG / MNC names and scam-style phrases.
          </div>
          <label style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, display: "block", marginBottom: 6 }}>
            Company name
          </label>
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setCheck(null);
              setError("");
            }}
            onBlur={() => { if (draft.trim()) runCheck(draft); }}
            placeholder="e.g. Zoho, Freshworks, Atlassian…"
            style={{
              width: "100%",
              padding: "11px 12px",
              fontSize: 14,
              border: `1.5px solid ${C.border}`,
              borderRadius: 10,
              boxSizing: "border-box",
              marginBottom: 10,
            }}
          />

          {(error || check?.warnings?.length) && (
            <div style={{
              padding: "10px 12px",
              borderRadius: 8,
              marginBottom: 10,
              background: check?.severity === "block" || error ? "#FEF2F2" : "#E7F0F6",
              border: `1px solid ${check?.severity === "block" || error ? "#FECACA" : "#BFDBFE"}`,
              fontSize: 12.5,
              color: check?.severity === "block" || error ? "#B91C1C" : "#143B56",
              lineHeight: 1.45,
            }}>
              {error || check.warnings.join(" ")}
              {check?.suggestedOriginal && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      pick(check.suggestedOriginal);
                      setModalOpen(false);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 7,
                      border: "1px solid #1B4F72",
                      background: "#E7F0F6",
                      color: "#143B56",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    Use “{check.suggestedOriginal}” instead
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={saving}
              onClick={() => setModalOpen(false)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: "#fff",
                color: C.muted,
                fontWeight: 650,
                fontSize: 12.5,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            {(check?.needsConfirm || check?.severity === "warn") && (
              <button
                type="button"
                disabled={saving}
                onClick={() => saveOther(true)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "#143B56",
                  color: "#fff",
                  fontWeight: 750,
                  fontSize: 12.5,
                  cursor: "pointer",
                }}
              >
                {saving ? "Saving…" : "Add anyway"}
              </button>
            )}
            <button
              type="button"
              disabled={saving || check?.severity === "block"}
              onClick={() => saveOther(false)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "none",
                background: C.primary,
                color: "#fff",
                fontWeight: 750,
                fontSize: 12.5,
                cursor: saving || check?.severity === "block" ? "not-allowed" : "pointer",
                opacity: check?.severity === "block" ? 0.5 : 1,
              }}
            >
              {saving ? "Saving…" : "Save for everyone"}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...inputStyle,
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          cursor: "pointer",
          background: "#fff",
        }}
      >
        <span style={{
          color: company ? C.text : C.muted,
          fontWeight: company ? 650 : 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {display}
          {isCustom ? " · Community" : ""}
        </span>
        <span
          aria-hidden
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            background: C.warningBg || "#E7F0F6",
            color: C.warning || "#1B4F72",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={`ti ti-chevron-${open ? "up" : "down"}`} style={{ fontSize: 14 }} />
        </span>
      </button>
      {menu}
      {modal}
    </div>
  );
}
